import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import ReportFilter from './components/ReportFilter';
import { formatTime } from '../common/util/formatter';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import usePositionAttributes from '../common/attributes/usePositionAttributes';
import { useCatch } from '../reactHelper';
import { useAttributePreference } from '../common/util/preferences';
import {
  altitudeFromMeters,
  distanceFromMeters,
  speedFromKnots,
  speedToKnots,
  volumeFromLiters,
} from '../common/util/converter';
import useReportStyles from './common/useReportStyles';
import fetchOrThrow from '../common/util/fetchOrThrow';
import SelectField from '../common/components/SelectField';

const to2 = (n) => Number(Number(n).toFixed(2));
const asArray = (v) => (Array.isArray(v) ? v : [v]);

const ChartReportPage = () => {
  const { classes } = useReportStyles();
  const theme = useTheme();
  const t = useTranslation();

  const positionAttributes = usePositionAttributes(t);

  const distanceUnit = useAttributePreference('distanceUnit');
  const altitudeUnit = useAttributePreference('altitudeUnit');
  const speedUnit = useAttributePreference('speedUnit');
  const volumeUnit = useAttributePreference('volumeUnit');

  const [items, setItems] = useState([]);
  const [types, setTypes] = useState(['speed']);
  const [selectedTypes, setSelectedTypes] = useState(['speed']);
  const [timeType, setTimeType] = useState('fixTime');

  // ✅ Evita erro "Cannot assign to read only property" (Recharts/React 19 + dados imutáveis)
  // Sempre passe dados mutáveis pro chart.
  const chartData = useMemo(() => items.map((p) => ({ ...p })), [items]);

  const getUnit = useCallback(
    (key) => {
      const dt = positionAttributes[key]?.dataType;
      if (dt === 'speed') return speedUnit;
      if (dt === 'distance') return distanceUnit;
      if (dt === 'altitude') return altitudeUnit;
      if (dt === 'volume') return volumeUnit;
      if (dt === 'hours') return 'h';
      return '';
    },
    [positionAttributes, speedUnit, distanceUnit, altitudeUnit, volumeUnit],
  );

  const formatValue = useCallback((value) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return value;
    const abs = Math.abs(value);
    if (abs >= 100) return value.toFixed(0);
    if (abs >= 10) return value.toFixed(1);
    return value.toFixed(2);
  }, []);

  const { minValue, maxValue, valueRange } = useMemo(() => {
    const activeTypes = selectedTypes?.length ? selectedTypes : [];
    const values = chartData.flatMap((it) =>
      activeTypes
        .map((type) => it?.[type])
        .filter((v) => typeof v === 'number' && Number.isFinite(v)),
    );

    if (!values.length) {
      return { minValue: 0, maxValue: 100, valueRange: 100 };
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;

    return { minValue: min, maxValue: max, valueRange: range === 0 ? 1 : range };
  }, [chartData, selectedTypes]);

  const onShow = useCatch(async ({ deviceIds, from, to }) => {
    const query = new URLSearchParams({ from, to });
    deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));

    const response = await fetchOrThrow(`/api/reports/route?${query.toString()}`, {
      headers: { Accept: 'application/json' },
    });

    const positions = await response.json();

    const keySet = new Set();
    const keyList = [];

    const formattedPositions = positions.map((position) => {
      const data = { ...position, ...position.attributes };
      const formatted = {};

      formatted.fixTime = dayjs(position.fixTime).valueOf();
      formatted.deviceTime = dayjs(position.deviceTime).valueOf();
      formatted.serverTime = dayjs(position.serverTime).valueOf();

      Object.keys(data)
        .filter((key) => !['id', 'deviceId'].includes(key))
        .forEach((key) => {
          const value = data[key];

          if (typeof value === 'number' && Number.isFinite(value)) {
            keySet.add(key);

            const definition = positionAttributes[key] || {};
            switch (definition.dataType) {
              case 'speed':
                if (key === 'obdSpeed') {
                  formatted[key] = to2(
                    speedFromKnots(speedToKnots(value, 'kmh'), speedUnit),
                  );
                } else {
                  formatted[key] = to2(speedFromKnots(value, speedUnit));
                }
                break;

              case 'altitude':
                formatted[key] = to2(altitudeFromMeters(value, altitudeUnit));
                break;

              case 'distance':
                formatted[key] = to2(distanceFromMeters(value, distanceUnit));
                break;

              case 'volume':
                formatted[key] = to2(volumeFromLiters(value, volumeUnit));
                break;

              case 'hours':
                formatted[key] = to2(value / 1000);
                break;

              default:
                formatted[key] = value;
                break;
            }
          }
        });

      return { ...formatted };
    });

    Object.keys(positionAttributes).forEach((key) => {
      if (keySet.has(key)) {
        keyList.push(key);
        keySet.delete(key);
      }
    });

    const allTypes = [...keyList, ...Array.from(keySet)];
    setTypes(allTypes);
    setItems(formattedPositions);

    // garante seleção válida depois de carregar
    if (!selectedTypes?.length) {
      setSelectedTypes(allTypes.length ? [allTypes[0]] : ['speed']);
    } else if (allTypes.length && selectedTypes.some((k) => !allTypes.includes(k))) {
      setSelectedTypes([allTypes[0]]);
    }
  });

  const colorPalette = useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.success.main,
      theme.palette.text.secondary,
    ],
    [theme],
  );

  const tooltipContentStyle = useMemo(
    () => ({
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
      boxShadow: theme.shadows[6],
      padding: '10px 12px',
    }),
    [theme],
  );

  const safeSelectedTypes = selectedTypes?.length ? selectedTypes : [];

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportChart']}>
      <ReportFilter onShow={onShow} deviceType="single">
        <div className={classes.filterItem}>
          <SelectField
            label={t('reportChartType')}
            value={selectedTypes}
            onChange={(e) => setSelectedTypes(asArray(e.target.value))}
            multiple
            disabled={!items.length}
            fullWidth
            searchPlaceholder={t('sharedSearch')}
            data={types.map((key) => ({ key, title: positionAttributes[key]?.name || key }))}
            keyGetter={(item) => item.key}
            titleGetter={(item) => item.title}
          />
        </div>

        <div className={classes.filterItem}>
          <SelectField
            label={t('reportTimeType')}
            value={timeType}
            onChange={(e) => setTimeType(e.target.value)}
            disabled={!items.length}
            fullWidth
            searchPlaceholder={t('sharedSearch')}
            data={[
              { key: 'fixTime', title: t('positionFixTime') },
              { key: 'deviceTime', title: t('positionDeviceTime') },
              { key: 'serverTime', title: t('positionServerTime') },
            ]}
            keyGetter={(item) => item.key}
            titleGetter={(item) => item.title}
          />
        </div>
      </ReportFilter>

      {items.length > 0 ? (
        // ✅ Evita erro width/height -1 no Recharts: container precisa ter altura/largura reais
        <div
          className={classes.chart}
          style={{
            height: '60vh',
            minHeight: 320,
            width: '100%',
            minWidth: 0,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{
                top: 10, right: 40, left: 0, bottom: 10,
              }}
            >
              <XAxis
                stroke={theme.palette.text.primary}
                tickLine={false}
                axisLine={false}
                dataKey={timeType}
                type="number"
                tickFormatter={(value) => formatTime(value, 'time')}
                domain={['dataMin', 'dataMax']}
                scale="time"
              />

              <YAxis
                stroke={theme.palette.text.primary}
                tickLine={false}
                axisLine={false}
                type="number"
                tickFormatter={(value) => formatValue(value)}
                domain={[minValue - valueRange / 5, maxValue + valueRange / 5]}
              />

              <CartesianGrid
                stroke={theme.palette.divider}
                strokeOpacity={0.5}
                strokeDasharray="4 6"
              />

              <Tooltip
                contentStyle={tooltipContentStyle}
                labelStyle={{ color: theme.palette.text.secondary }}
                itemStyle={{ paddingTop: 2, paddingBottom: 2 }}
                formatter={(value, key) => {
                  const name = positionAttributes[key]?.name || key;
                  const unit = getUnit(key);
                  return [`${formatValue(value)}${unit ? ` ${unit}` : ''}`, name];
                }}
                labelFormatter={(value) => formatTime(value, 'seconds')}
              />

              <Legend
                verticalAlign="top"
                align="left"
                iconType="circle"
                wrapperStyle={{ paddingBottom: 8 }}
                formatter={(value) => positionAttributes[value]?.name || value}
              />

              {safeSelectedTypes.map((type, index) => (
                <Line
                  key={type}
                  type="monotone"
                  dataKey={type}
                  stroke={colorPalette[index % colorPalette.length]}
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  dot={false}
                  activeDot={{ r: 5 }}
                  connectNulls
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <Box sx={{ py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t('reportNoData') || 'Nenhum dado para exibir.'}
          </Typography>
        </Box>
      )}
    </PageLayout>
  );
};

export default ChartReportPage;
